import { cn } from '@/lib/utils';
import type { LLMultipleChoiceElement } from '../../types';

interface Props {
  element: LLMultipleChoiceElement;
  selectedOptionId: string | null;
  onChange: (optionId: string) => void;
  disabled?: boolean;
}

/**
 * Single-select question rendered as a button-list. The `question_type=question`
 * variant is graded server-side; `poll` and `survey` variants only require that
 * an option be picked. The button visuals are identical — the gating differs.
 */
export function MultipleChoiceElement({ element, selectedOptionId, onChange, disabled }: Props) {
  const groupName = `ll-mc-${element.index}`;
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{element.prompt}</legend>
      <div role="radiogroup" aria-labelledby={groupName} className="space-y-1.5">
        {element.options.map((opt) => {
          const id = `${groupName}-${opt.optionId}`;
          const selected = opt.optionId === selectedOptionId;
          return (
            <label
              key={opt.optionId}
              htmlFor={id}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer',
                'hover:bg-accent transition-colors',
                selected && 'border-primary bg-accent',
                disabled && 'opacity-60 cursor-not-allowed pointer-events-none',
              )}
            >
              <input
                id={id}
                type="radio"
                name={groupName}
                value={opt.optionId}
                checked={selected}
                onChange={() => onChange(opt.optionId)}
                disabled={disabled}
                className="h-4 w-4 accent-primary"
              />
              <span>{opt.value}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
